
import { useState, useCallback, useEffect } from "react";

// Session storage key used in App.tsx for loading screen
const LOADING_SHOWN_KEY = 'homeplate_loading_shown';
const TRANSITION_KEY = "hasSeenTransition";
const TIME_PREFERENCE_KEY = "timePreferenceSet";
const TIME_SELECTION_KEY = "selectedTimePreference";

const getFromStorage = (key: string, defaultValue: string = ""): string => {
  try {
    // Check both localStorage and sessionStorage
    const fromLocal = localStorage.getItem(key);
    const fromSession = sessionStorage.getItem(key);
    
    // Prefer localStorage but fall back to sessionStorage
    return fromLocal || fromSession || defaultValue;
  } catch (e) {
    console.error(`Error reading from storage for key ${key}:`, e);
    return defaultValue;
  }
};

const setToStorage = (key: string, value: string): void => {
  try {
    // Set to both localStorage and sessionStorage for reliability
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch (e) {
    console.error(`Error writing to storage for key ${key}:`, e);
  }
};

export const useHomePageOnboarding = (setActiveFilters: (fn: (prev: any) => any) => void) => {
  // Initialize state with a callback to get storage values
  const [showTransition, setShowTransition] = useState(() => {
    // Don't show transition if:
    // 1. User has seen it before (in any session)
    // 2. Loading screen has been shown in this session
    const hasSeenTransition = getFromStorage(TRANSITION_KEY) === "true";
    const hasSeenLoadingThisSession = sessionStorage.getItem(LOADING_SHOWN_KEY) === "true";
    
    return !hasSeenTransition && !hasSeenLoadingThisSession;
  });

  const [showOnboarding, setShowOnboarding] = useState(() => {
    const timePreferenceSet = getFromStorage(TIME_PREFERENCE_KEY) === "true";
    return !timePreferenceSet;
  });

  const [selectedTimePreference, setSelectedTimePreference] = useState<string>(() => 
    getFromStorage(TIME_SELECTION_KEY)
  );

  // Force a recheck of storage on mount to handle cross-tab scenarios
  useEffect(() => {
    // Secondary check to ensure state is correct after mount
    const hasSeenTransition = getFromStorage(TRANSITION_KEY) === "true";
    const hasSeenLoadingThisSession = sessionStorage.getItem(LOADING_SHOWN_KEY) === "true";
    const timePreferenceSet = getFromStorage(TIME_PREFERENCE_KEY) === "true";
    
    if ((hasSeenTransition || hasSeenLoadingThisSession) && showTransition) {
      console.log("Storage check: Resetting showTransition to false");
      setShowTransition(false);
    }
    
    if (timePreferenceSet && showOnboarding) {
      console.log("Storage check: Resetting showOnboarding to false");
      setShowOnboarding(false);
    }
  }, [showTransition, showOnboarding]);

  const handleTransitionComplete = useCallback(() => {
    console.log("handleTransitionComplete called");
    setShowTransition(false);
    setToStorage(TRANSITION_KEY, "true");
    // Also mark loading as shown since they've seen the transition
    sessionStorage.setItem(LOADING_SHOWN_KEY, "true");
    
    // Check if we need to show onboarding
    if (getFromStorage(TIME_PREFERENCE_KEY) !== "true") {
      console.log("Setting showOnboarding to true after transition");
      setShowOnboarding(true);
    }
  }, []);

  const handleTimePreference = useCallback((preference: string) => {
    if (!preference) {
      console.error("No preference selected");
      return;
    }

    console.log("Setting time preference filters:", preference);
    setSelectedTimePreference(preference);
    
    const today = new Date();
    let dateRange;

    switch (preference.toLowerCase()) {
      case "today":
        dateRange = { from: today, to: today };
        break;
      case "this week":
        const toThisWeek = new Date(today);
        toThisWeek.setDate(today.getDate() + (6 - today.getDay()));
        dateRange = { from: today, to: toThisWeek };
        break;
      case "next week":
        const fromNextWeek = new Date(today);
        fromNextWeek.setDate(today.getDate() + (7 - today.getDay()));
        const toNextWeek = new Date(fromNextWeek);
        toNextWeek.setDate(fromNextWeek.getDate() + 6);
        dateRange = { from: fromNextWeek, to: toNextWeek };
        break;
      default:
        console.error("Invalid preference:", preference);
        return;
    }

    setActiveFilters(prev => ({
      ...prev,
      dateRange
    }));

    setToStorage(TIME_PREFERENCE_KEY, "true");
    setToStorage(TIME_SELECTION_KEY, preference);
    
    // Also mark loading as shown since they've completed onboarding
    sessionStorage.setItem(LOADING_SHOWN_KEY, "true");
    
    // Don't show transition again here - it causes issues
    setShowOnboarding(false);
  }, [setActiveFilters]);

  return {
    showTransition,
    showOnboarding,
    setShowOnboarding,
    handleTransitionComplete,
    handleTimePreference,
    selectedTimePreference,
  };
};
